import Image from 'next/image'
import Section from 'components/common/Section'
import { SectionHeader } from 'components/marketing/SectionHeader'
import { Marketing } from 'designsystem/Text'
import cn from 'classnames'
import ExternalIcon from 'icons/ExternalIcon'
import { ComponentFeaturesWithImagesAlternatingSide } from 'types/generated/contentful'
import { useThemeColor } from './ThemeContext'

export const FeaturesWithImagesAlternatingSide = ({
  label,
  headline,
  subheadline,
  featuresCollection,
}: ComponentFeaturesWithImagesAlternatingSide): JSX.Element => {
  const themeColor = useThemeColor()

  return (
    <Section bgColor='gray'>
      <SectionHeader
        label={label!}
        title={headline!}
        subline={subheadline!}
        highlight={themeColor}
      />

      <ul className='two-column-layout space-y-16 md:space-y-20 lg:space-y-0 lg:gap-y-40'>
        {featuresCollection!.items!.map((feature, index) => {
          const { icon, title, text, image, linkLabel, linkUrl } = feature!
          const even = index % 2 === 0
          const side = even ? 'right' : 'left'

          return (
            <li
              key={title! + index}
              className='col-span-2 two-column-layout space-y-12 lg:space-y-0 lg:gap-y-40'
            >
              <div
                className={cn('flex flex-col items-start justify-center', {
                  'lg:order-2': even,
                  'lg:pr-32': side === 'left',
                  'lg:pl-32': side === 'right',
                })}
              >
                {icon?.url && (
                  <div className='h-12 w-12 relative lg:h-20 lg:w-20'>
                    <Image fill src={icon!.url!} alt={icon!.title!} />
                  </div>
                )}

                <Marketing.H3 className='spacer-xs'>{title}</Marketing.H3>

                <div className='spacer-2xs large-body-text'>
                  <p className='opacity-60'>{text}</p>
                  {linkLabel && (
                    <div className='mt-4'>
                      <a
                        href={linkUrl!}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-2 text-18 text-neutral-60 font-demibold'
                      >
                        {linkLabel}
                        <ExternalIcon />
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div className={`${even ? '' : 'lg:order-1'} w-full relative`}>
                <Image
                  width={image!.width!}
                  height={image!.height!}
                  src={image!.url!}
                  alt={image!.title!}
                />
              </div>
            </li>
          )
        })}
      </ul>
    </Section>
  )
}
